<template>
    <div class="row no-gutters datepicker mb-0">
        <date-picker
            ref="start_date"
            v-validate="rules"
            v-bind="$attrs"
            :value="myDate"
            :config="options"
            autocomplete="off"
            @dp-change="handleDatePickerChange"
            @dp-show="showDp"
            :class="{
                'border-danger':
                    errors.has('date') || formErrors.includes('date')
            }"
            :placeholder="placeholder.date"
            class="form-control col-7 mr-1"
            name="date"
        ></date-picker>
        <b-input
            v-if="timeslot && isLoadingTimeslot"
            class="col-5 firefox-col-5 form-control"
            value="Loading..."
            readonly
            style="background-color: #fff;"
        />
        <b-select
            v-else-if="timeslot && timeslot.length > 0 && !isLoadingTimeslot"
            class="col-5 firefox-col-5 form-control"
            :options="timeslot"
            name="time"
            :disabled="isLoadingTimeslot"
            :value="myTime.toFormat('HHmm')"
            @change="onTimeChange"
        />
        <b-input
            v-else-if="timeslot && timeslot.length <= 0 && !isLoadingTimeslot"
            class="col-5 firefox-col-5 form-control border-danger"
            value="Not Available"
            readonly
        />
        <timepicker
            v-else
            ref="timepicker"
            :id="`timepicker-${$attrs.id}`"
            v-bind="$attrs"
            :value="myTime"
            @input="onTimeInput"
            :class="{
                'border-danger':
                    errors.has('time') || formErrors.includes('time')
            }"
            v-validate="rules"
            :placeholder="placeholder.time"
            name="time"
            :step="step"
            class="col-5 firefox-col-5 form-control"
        />
    </div>
</template>
<script>
import { DateTime } from "luxon";
import Timepicker from "./time-picker";

export default {
    name: "VetDateTimePicker",
    components: { Timepicker },
    props: {
        isLoadingTimeslot: {
            type: Boolean,
            default: false
        },
        timeslot: {
            type: Array,
            default: null
        },
        isOptional: {
            type: Boolean,
            default: false
        },
        value: [Object, Number],
        minTime: Object,
        maxTime: Object,
        rules: {
            type: [Object, String],
            default: ""
        },
        placeholder: {
            type: Object,
            default: function () {
                return { date: null, time: null };
            }
        },
        container: {
            type: String,
            required: true
        },
        innerClass: {
            type: Object,
            default: () => {}
        },
        format: {
            String,
            validator: function (value) {
                // The value must match one of these strings
                return ["moment", "iso", "datetime"].includes(
                    value.toLowerCase()
                );
            },
            default: "DateTime"
        },
        step: {
            type: Number,
            default: 5
        },
        isUpdatePosition: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            resources: {},
            options: {
                widgetParent: "#appointmentModal",
                format: "DD/MM/YYYY",
                widgetPositioning: { horizontal: "auto", vertical: "bottom" },
                timeZone: null,
                useCurrent: false,
                minDate: false,
                maxDate: false
            },
            show: true,
            myDateTime: null,
            myDate: null,
            myTime: null,
            formErrors: []
        };
    },
    watch: {
        minTime: {
            handler(value) {
                if (value) {
                    this.options.minDate = this._fromDateTime(value, "moment");
                }
            },
            immediate: true
        },
        maxTime: {
            handler(value) {
                if (value) {
                    this.options.maxDate = this._fromDateTime(value, "moment");
                }
            },
            immediate: true
        },
        value: {
            // trigger when this.$emit("input", this._fromDateTime(dt, this.format));
            // trigger when parent update value
            handler(value) {
                this.myDateTime = this._toDateTime(value);
                this.myDate = this._fromDateTime(this.myDateTime, "moment");
                this.myTime = this.myDateTime;
            },
            immediate: true
        },
        innerClass(value) {
            this.formErrors = [];
            if (value["border-danger"]) {
                if (!this.myDate) {
                    this.formErrors.push("date");
                } else if (!this.myTime) {
                    this.formErrors.push("time");
                } else {
                    this.formErrors.push("date");
                    this.formErrors.push("time");
                }
            }
        }
    },
    computed: {
        returnTime() {
            return !!this.myTime;
        },
        timePickerDisabled() {
            if (this.isOptional && !this.myDate) {
                return true;
            }
            return false;
        }
    },
    created() {
        this.options.timeZone = this._timeZone;
        this.options.format = this.getDateFormat();
        this.options.widgetParent = this.container || null;
        this.options.minDate = this.minTime
            ? this._fromDateTime(this.minTime, "moment")
            : false;
    },
    methods: {
        resetOptional(source) {
            let reset = false;

            if (!this.isOptional) {
                return;
            }

            if (source === "time") {
                this.myTime = null;
                if (!this.myTime && !this.myDate) {
                    reset = true;
                }
            } else if (source === "date") {
                this.myDate = null;
                if (!this.myTime && !this.myDate) {
                    reset = true;
                }
            }

            if (reset) {
                this.myDateTime = null;
                this.$emit("input", null);
            }
        },
        handleDatePickerChange(event) {
            if (!event.target.value) {
                this.resetOptional("time");
            }
            this.myDate = event.date;
            this.generateDateTime();
        },

        generateDateTime() {
            let isHasDateOrTime = false;
            let date = null,
                time = null,
                datetime = null;

            if (this.myDate) {
                isHasDateOrTime = true;
                date = moment.isMoment(this.myDate)
                    ? this.myDate
                    : moment(this.myDate, this.options.format);
            }

            if (this.myTime) {
                isHasDateOrTime = true;
                time = this._fromDateTime(this.myTime, "moment");
            }

            if (date && time) {
                datetime = DateTime.fromObject({
                    year: date.format("YYYY"),
                    month: date.format("MM"),
                    day: date.format("D"),
                    hour: time.format("HH"),
                    minute: time.format("mm"),
                    zone: this._timeZone
                });
            }

            this.myDateTime = datetime;
            this.$emit("update-required", isHasDateOrTime);
            this.$emit("input", this._fromDateTime(datetime, this.format));
        },
        showDp(event) {
            if (this.timeslot) {
                // timeslot will hide the timepicker,
                // so we skip the function.
                this._datePickerShow(event, false, this.isUpdatePosition);
                return;
            }

            //debugger here if want to stop ui
            this.$refs["timepicker"].hide();
            if (this.maxTime || this.minTime) {
                this.$refs["start_date"].$once("dp-error", event => {
                    this.myDate = event.date;
                });
            }

            return this._datePickerShow(event, false, this.isUpdatePosition);
        },
        onTimeChange(value) {
            this.$emit("timeslot-change", value);
        },
        onTimeInput(time) {
            this.myTime = time;
            if (!this.myTime) {
                this.resetOptional("date");
            }
            this.generateDateTime();
        }
    }
};
</script>
